import * as React from 'react';
import { useCallback } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';

import AircraftPopupContent, { Props as AircraftPopupContentProps } from './AircraftPopupContent/AircraftPopupContent';
import SimpleLoader from '../../SimpleLoader';
import { useAircraftInfoLazyQuery } from '@websky/graphql';
import Button from '../../Button';
import { useToggleable } from '../../hooks';
import { useTheme } from '../../theme';
import { InfoIcon } from '../../FlightCard/components/Icons';
import ModalConfirm from '../../ModalConfirm';

interface Props extends Partial<AircraftPopupContentProps> {
	className?: string;
	id: string;
	operatingIata?: string;
	name: string;
	renderButton?: (onClick: () => void, loading: boolean) => React.ReactNode;
	renderInBody?: boolean;
}

const AircraftPopup: React.FC<Props> = ({ className, id, operatingIata, name, renderButton, renderInBody }) => {
	const { AircraftPopup: css } = useTheme('AircraftPopup');
	const { t } = useTranslation('Checkout');

	const { isOpen, open, close } = useToggleable(false);

	const [aircraftInfo, { data, loading }] = useAircraftInfoLazyQuery({
		variables: { id, operatingAirlineIata: operatingIata }
	});

	const onOpen = useCallback(async () => {
		await aircraftInfo();
		open();
	}, [aircraftInfo, open]);

	const renderModal = () => {
		return (
			<ModalConfirm
				classes={{
					paper: css.paper
				}}
				isOpen={isOpen}
				onClose={close}
				size="xlarge"
				noPadding={true}
				hideFooter={true}
				hideTitle={true}
			>
				<AircraftPopupContent name={name} aircraftInfo={data?.AircraftInfo} onClose={close} />
			</ModalConfirm>
		);
	};

	return (
		<>
			{loading && <SimpleLoader />}

			{renderButton?.(onOpen, loading) ?? (
				<Button className={cn(className, css.button)} variant="text" onClick={onOpen}>
					{t('Aircraft info')}
					<span className={css.icon}>{InfoIcon}</span>
				</Button>
			)}

			{data?.AircraftInfo && isOpen && (
				<>{renderInBody ? createPortal(renderModal(), document.body) : renderModal()}</>
			)}
		</>
	);
};

export default AircraftPopup;
